import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Upload,
  Database,
  Play,
  Square,
  BookOpen,
  FolderTree,
  FileCode,
  Cpu,
  Info,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Copy,
  Check,
  Zap,
  Box,
  Code2,
  FileType,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import {
  TritonInstanceDetails,
  mockTritonInstanceDetails,
} from '@/mock/gpu-instance';
import { cn } from '@/lib/utils';
import UploadModelDialog from '../../components/model-management/UploadModelDialog';

// Supported backends with detailed info
const SUPPORTED_BACKENDS = [
  {
    id: 'onnxruntime',
    name: 'ONNX Runtime',
    description: 'Cross-platform ML inference engine',
    fileFormats: ['.onnx'],
    frameworks: ['PyTorch', 'TensorFlow', 'Scikit-learn', 'Keras'],
    configExample: `name: "my_onnx_model"
platform: "onnxruntime_onnx"
max_batch_size: 8
input [
  {
    name: "input"
    data_type: TYPE_FP32
    dims: [ -1, 3, 224, 224 ]
  }
]
output [
  {
    name: "output"
    data_type: TYPE_FP32
    dims: [ -1, 1000 ]
  }
]
instance_group [
  {
    count: 1
    kind: KIND_GPU
  }
]`,
    notes: 'Most versatile backend. Export from PyTorch: torch.onnx.export()',
  },
  {
    id: 'tensorrt',
    name: 'TensorRT',
    description: 'NVIDIA optimized inference engine',
    fileFormats: ['.plan', '.engine'],
    frameworks: ['TensorRT', 'ONNX (converted)'],
    configExample: `name: "my_tensorrt_model"
platform: "tensorrt_plan"
max_batch_size: 16
input [
  {
    name: "input"
    data_type: TYPE_FP32
    dims: [ 3, 224, 224 ]
  }
]
output [
  {
    name: "output"
    data_type: TYPE_FP32
    dims: [ 1000 ]
  }
]
instance_group [
  {
    count: 1
    kind: KIND_GPU
  }
]`,
    notes: 'Best performance on NVIDIA GPUs. Use trtexec to convert ONNX.',
  },
  {
    id: 'pytorch',
    name: 'PyTorch (TorchScript)',
    description: 'Native PyTorch model support',
    fileFormats: ['.pt', '.pth'],
    frameworks: ['PyTorch'],
    configExample: `name: "my_pytorch_model"
platform: "pytorch_libtorch"
max_batch_size: 8
input [
  {
    name: "INPUT__0"
    data_type: TYPE_FP32
    dims: [ 3, 224, 224 ]
  }
]
output [
  {
    name: "OUTPUT__0"
    data_type: TYPE_FP32
    dims: [ 1000 ]
  }
]
instance_group [
  {
    count: 1
    kind: KIND_GPU
  }
]`,
    notes: 'Use torch.jit.script() or torch.jit.trace() to export.',
  },
  {
    id: 'python',
    name: 'Python Backend',
    description: 'Custom Python inference logic',
    fileFormats: ['model.py'],
    frameworks: ['Any Python library'],
    configExample: `name: "my_python_model"
backend: "python"
max_batch_size: 4
input [
  {
    name: "text"
    data_type: TYPE_STRING
    dims: [ -1 ]
  }
]
output [
  {
    name: "embedding"
    data_type: TYPE_FP32
    dims: [ -1, 768 ]
  }
]
instance_group [
  {
    count: 1
    kind: KIND_GPU
  }
]`,
    notes: 'Requires model.py with TritonPythonModel class. Most flexible.',
  },
  {
    id: 'openvino',
    name: 'OpenVINO',
    description: 'Intel optimized inference',
    fileFormats: ['.xml', '.bin'],
    frameworks: ['OpenVINO IR', 'ONNX (converted)'],
    configExample: `name: "my_openvino_model"
platform: "openvino"
max_batch_size: 8
input [
  {
    name: "input"
    data_type: TYPE_FP32
    dims: [ 3, 224, 224 ]
  }
]
output [
  {
    name: "output"
    data_type: TYPE_FP32
    dims: [ 1000 ]
  }
]
instance_group [
  {
    count: 1
    kind: KIND_CPU
  }
]`,
    notes: 'Optimized for Intel CPUs. Use Model Optimizer to convert.',
  },
];

const DATA_TYPES = [
  { type: 'TYPE_BOOL', description: 'Boolean' },
  { type: 'TYPE_UINT8', description: '8-bit unsigned integer' },
  { type: 'TYPE_UINT16', description: '16-bit unsigned integer' },
  { type: 'TYPE_UINT32', description: '32-bit unsigned integer' },
  { type: 'TYPE_UINT64', description: '64-bit unsigned integer' },
  { type: 'TYPE_INT8', description: '8-bit signed integer' },
  { type: 'TYPE_INT16', description: '16-bit signed integer' },
  { type: 'TYPE_INT32', description: '32-bit signed integer' },
  { type: 'TYPE_INT64', description: '64-bit signed integer' },
  { type: 'TYPE_FP16', description: '16-bit floating point (half)' },
  { type: 'TYPE_FP32', description: '32-bit floating point (float)' },
  { type: 'TYPE_FP64', description: '64-bit floating point (double)' },
  { type: 'TYPE_STRING', description: 'String/bytes' },
];

const GPUInstanceModelManagement = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [selectedBackend, setSelectedBackend] = useState('onnxruntime');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['structure']));
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const details: TritonInstanceDetails = useMemo(
    () => mockTritonInstanceDetails(instanceId || ''),
    [instanceId]
  );

  const { modelManagement } = details;

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const currentBackend = SUPPORTED_BACKENDS.find((b) => b.id === selectedBackend);

  return (
    <div className="container py-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Upload className="h-6 w-6" />
          Model Management
        </h1>
        <p className="text-muted-foreground">
          Load, unload, and upload models to Triton Inference Server
        </p>
      </div>

      {/* Quick Upload Card */}
      <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-transparent">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Quick Upload</h3>
                <p className="text-sm text-muted-foreground">
                  Upload a new model with guided configuration
                </p>
              </div>
            </div>
            <Button onClick={() => setUploadDialogOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Upload Model
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Quick API Reference */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">API Endpoints</CardTitle>
          <CardDescription>
            Runtime model control via HTTP API
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="p-3 bg-muted/30 rounded-lg space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Database className="h-4 w-4" />
                List Models
              </div>
              <div className="bg-[#1a1b26] text-gray-300 p-2 rounded text-xs font-mono overflow-hidden">
                <div className="text-green-400">POST</div>
                <div className="truncate">{modelManagement.endpoints.index}</div>
              </div>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Play className="h-4 w-4 text-green-500" />
                Load Model
              </div>
              <div className="bg-[#1a1b26] text-gray-300 p-2 rounded text-xs font-mono overflow-hidden">
                <div className="text-green-400">POST</div>
                <div className="truncate">{modelManagement.endpoints.load}</div>
              </div>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Square className="h-4 w-4 text-red-500" />
                Unload Model
              </div>
              <div className="bg-[#1a1b26] text-gray-300 p-2 rounded text-xs font-mono overflow-hidden">
                <div className="text-green-400">POST</div>
                <div className="truncate">{modelManagement.endpoints.unload}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Supported Backends */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Cpu className="h-5 w-5" />
            Supported Backends & Model Configuration
          </CardTitle>
          <CardDescription>
            Choose a backend to see supported file formats and config.pbtxt example
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Backend Tabs */}
          <Tabs value={selectedBackend} onValueChange={setSelectedBackend}>
            <TabsList className="grid grid-cols-5 w-full">
              {SUPPORTED_BACKENDS.map((backend) => (
                <TabsTrigger key={backend.id} value={backend.id} className="text-xs">
                  {backend.name.split(' ')[0]}
                </TabsTrigger>
              ))}
            </TabsList>

            {SUPPORTED_BACKENDS.map((backend) => (
              <TabsContent key={backend.id} value={backend.id} className="space-y-4 mt-4">
                {/* Backend Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                      <div>
                        <div className="text-sm font-medium">{backend.name}</div>
                        <div className="text-xs text-muted-foreground">{backend.description}</div>
                      </div>

                      <div>
                        <div className="text-xs font-medium text-muted-foreground mb-1">Supported File Formats</div>
                        <div className="flex flex-wrap gap-1">
                          {backend.fileFormats.map((format) => (
                            <Badge key={format} variant="secondary" className="text-xs font-mono">
                              {format}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="text-xs font-medium text-muted-foreground mb-1">Compatible Frameworks</div>
                        <div className="flex flex-wrap gap-1">
                          {backend.frameworks.map((fw) => (
                            <Badge key={fw} variant="outline" className="text-xs">
                              {fw}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="pt-2 border-t">
                        <div className="flex items-start gap-2">
                          <Info className="h-3 w-3 text-blue-500 mt-0.5 shrink-0" />
                          <div className="text-xs text-muted-foreground">{backend.notes}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Config Example */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium flex items-center gap-2">
                        <FileCode className="h-4 w-4" />
                        config.pbtxt Example
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => handleCopy(backend.configExample, `config-${backend.id}`)}
                      >
                        {copiedField === `config-${backend.id}` ? (
                          <><Check className="h-3 w-3 mr-1" /> Copied</>
                        ) : (
                          <><Copy className="h-3 w-3 mr-1" /> Copy</>
                        )}
                      </Button>
                    </div>
                    <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto max-h-[300px] overflow-y-auto">
                      {backend.configExample}
                    </pre>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Model Repository Structure */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FolderTree className="h-5 w-5" />
            Model Repository Structure
          </CardTitle>
          <CardDescription>
            Required directory structure for uploading models
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Structure Diagram */}
            <div className="space-y-3">
              <div className="text-sm font-medium">Directory Layout</div>
              <div className="bg-[#1a1b26] rounded-lg p-4 font-mono text-sm">
                <div className="text-gray-400">{modelManagement.repositoryPath}/</div>
                <div className="pl-4 text-blue-400">my_model/</div>
                <div className="pl-8 text-green-400">config.pbtxt</div>
                <div className="pl-8 text-gray-500"># Model configuration (required)</div>
                <div className="pl-8 text-yellow-400">1/</div>
                <div className="pl-12 text-gray-500"># Version directory</div>
                <div className="pl-12 text-cyan-400">model.onnx</div>
                <div className="pl-12 text-gray-500"># or model.plan, model.pt, model.py</div>
                <div className="pl-8 text-yellow-400">2/</div>
                <div className="pl-12 text-gray-500"># Optional: multiple versions</div>
                <div className="pl-12 text-cyan-400">model.onnx</div>
              </div>
            </div>

            {/* Key Points */}
            <div className="space-y-3">
              <div className="text-sm font-medium">Key Requirements</div>
              <div className="space-y-2">
                <div className="flex items-start gap-2 p-2 bg-muted/30 rounded">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <div className="text-xs">
                    <span className="font-medium">config.pbtxt</span> - Required at model root. Defines inputs, outputs, and backend.
                  </div>
                </div>
                <div className="flex items-start gap-2 p-2 bg-muted/30 rounded">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <div className="text-xs">
                    <span className="font-medium">Version directories</span> - Numeric folders (1/, 2/) containing model files.
                  </div>
                </div>
                <div className="flex items-start gap-2 p-2 bg-muted/30 rounded">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <div className="text-xs">
                    <span className="font-medium">Model file naming</span> - Must match backend: model.onnx, model.plan, model.pt, model.py
                  </div>
                </div>
                <div className="flex items-start gap-2 p-2 bg-amber-500/10 rounded border border-amber-500/20">
                  <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <div className="text-xs">
                    <span className="font-medium text-amber-600">Input/Output dims</span> - Use -1 for dynamic batch dimension. Must match your model exactly.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Types Reference */}
      <Collapsible open={expandedSections.has('datatypes')} onOpenChange={() => toggleSection('datatypes')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="pb-3 cursor-pointer hover:bg-muted/30 transition-colors">
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FileType className="h-5 w-5" />
                  Supported Data Types
                </span>
                {expandedSections.has('datatypes') ? (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                )}
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {DATA_TYPES.map((dt) => (
                  <div key={dt.type} className="p-2 bg-muted/30 rounded text-xs">
                    <code className="text-blue-500 font-mono">{dt.type}</code>
                    <div className="text-muted-foreground">{dt.description}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Upload Model API */}
      <Card className="border-blue-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Upload className="h-5 w-5 text-blue-500" />
            Upload New Model via API
          </CardTitle>
          <CardDescription>
            Upload model files directly using the load endpoint with base64-encoded content
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
              <div>
                Use the <code className="bg-muted px-1 rounded">file:</code> prefix in parameters to upload base64-encoded files.
                The path after <code className="bg-muted px-1 rounded">file:</code> determines where the file is placed in the model directory.
              </div>
            </div>
          </div>

          {/* Complete Upload Example */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Complete Upload Example (ONNX Model)</div>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => handleCopy(`curl -X POST "${modelManagement.endpoints.load.replace('{MODEL_NAME}', 'my-new-model')}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "parameters": {
      "config": "name: \\"my-new-model\\"\\nplatform: \\"onnxruntime_onnx\\"\\nmax_batch_size: 8\\ninput [{ name: \\"input\\", data_type: TYPE_FP32, dims: [-1, 3, 224, 224] }]\\noutput [{ name: \\"output\\", data_type: TYPE_FP32, dims: [-1, 1000] }]",
      "file:1/model.onnx": "'$(base64 -w0 model.onnx)'"
    }
  }'`, 'upload-example')}
              >
                {copiedField === 'upload-example' ? (
                  <><Check className="h-3 w-3 mr-1" /> Copied</>
                ) : (
                  <><Copy className="h-3 w-3 mr-1" /> Copy</>
                )}
              </Button>
            </div>
            <pre className="bg-[#1a1b26] text-gray-300 p-4 rounded-lg text-xs font-mono overflow-x-auto">
{`POST ${modelManagement.endpoints.load.replace('{MODEL_NAME}', 'my-new-model')}
Content-Type: application/json
Authorization: Bearer YOUR_API_KEY

{
  "parameters": {
    // config.pbtxt content as escaped string
    "config": "name: \\"my-new-model\\"\\n
               platform: \\"onnxruntime_onnx\\"\\n
               max_batch_size: 8\\n
               input [{ name: \\"input\\", data_type: TYPE_FP32, dims: [-1, 3, 224, 224] }]\\n
               output [{ name: \\"output\\", data_type: TYPE_FP32, dims: [-1, 1000] }]",

    // Model file: "file:<version>/<filename>": "<base64-content>"
    "file:1/model.onnx": "<base64-encoded-model-file>"
  }
}`}
            </pre>
          </div>

          {/* Python Example */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Python Upload Script</div>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => handleCopy(`import requests
import base64

# Read and encode model file
with open("model.onnx", "rb") as f:
    model_b64 = base64.b64encode(f.read()).decode("utf-8")

# Model configuration
config = """
name: "my-new-model"
platform: "onnxruntime_onnx"
max_batch_size: 8
input [
  { name: "input", data_type: TYPE_FP32, dims: [-1, 3, 224, 224] }
]
output [
  { name: "output", data_type: TYPE_FP32, dims: [-1, 1000] }
]
"""

# Upload model
response = requests.post(
    "${modelManagement.endpoints.load.replace('{MODEL_NAME}', 'my-new-model')}",
    headers={
        "Content-Type": "application/json",
        "Authorization": "Bearer YOUR_API_KEY"
    },
    json={
        "parameters": {
            "config": config,
            "file:1/model.onnx": model_b64
        }
    }
)

print(response.json())`, 'python-example')}
              >
                {copiedField === 'python-example' ? (
                  <><Check className="h-3 w-3 mr-1" /> Copied</>
                ) : (
                  <><Copy className="h-3 w-3 mr-1" /> Copy</>
                )}
              </Button>
            </div>
            <pre className="bg-[#1a1b26] text-gray-300 p-4 rounded-lg text-xs font-mono overflow-x-auto">
{`import requests
import base64

# Read and encode model file
with open("model.onnx", "rb") as f:
    model_b64 = base64.b64encode(f.read()).decode("utf-8")

# Model configuration
config = """
name: "my-new-model"
platform: "onnxruntime_onnx"
max_batch_size: 8
input [
  { name: "input", data_type: TYPE_FP32, dims: [-1, 3, 224, 224] }
]
output [
  { name: "output", data_type: TYPE_FP32, dims: [-1, 1000] }
]
"""

# Upload model
response = requests.post(
    "${modelManagement.endpoints.load.replace('{MODEL_NAME}', 'my-new-model')}",
    headers={
        "Content-Type": "application/json",
        "Authorization": "Bearer YOUR_API_KEY"
    },
    json={
        "parameters": {
            "config": config,
            "file:1/model.onnx": model_b64
        }
    }
)

print(response.json())`}
            </pre>
          </div>
        </CardContent>
      </Card>

      {/* Verify Model */}
      <Card className="border-green-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            Verify Model Configuration
          </CardTitle>
          <CardDescription>
            How to check if your model is correctly configured
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Check Model Status */}
            <div className="space-y-2">
              <div className="text-sm font-medium">1. Check Model Status</div>
              <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`GET /v2/models/{model_name}

Response:
{
  "name": "my-model",
  "versions": ["1"],
  "platform": "onnxruntime_onnx",
  "state": "READY"  // READY = success
}`}
              </pre>
            </div>

            {/* Check Model Config */}
            <div className="space-y-2">
              <div className="text-sm font-medium">2. Get Model Configuration</div>
              <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`GET /v2/models/{model_name}/config

Response:
{
  "name": "my-model",
  "platform": "onnxruntime_onnx",
  "input": [...],
  "output": [...]
}`}
              </pre>
            </div>
          </div>

          {/* Common Errors */}
          <div className="space-y-2">
            <div className="text-sm font-medium">Common Configuration Errors</div>
            <div className="grid gap-2">
              <div className="flex items-start gap-2 p-3 bg-red-500/5 rounded-lg border border-red-500/20">
                <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <div className="text-xs">
                  <span className="font-medium text-red-600">Dimension mismatch</span>
                  <p className="text-muted-foreground">Input/output dims in config.pbtxt must exactly match your model. Use tools like Netron to inspect ONNX model shapes.</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-red-500/5 rounded-lg border border-red-500/20">
                <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <div className="text-xs">
                  <span className="font-medium text-red-600">Wrong platform</span>
                  <p className="text-muted-foreground">Platform must match file type: onnxruntime_onnx for .onnx, tensorrt_plan for .plan, pytorch_libtorch for .pt</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-red-500/5 rounded-lg border border-red-500/20">
                <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <div className="text-xs">
                  <span className="font-medium text-red-600">Missing version directory</span>
                  <p className="text-muted-foreground">Model file must be in a version directory (e.g., 1/model.onnx), not directly in the model folder.</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <UploadModelDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        instanceId={instanceId || ''}
      />
    </div>
  );
};

export default GPUInstanceModelManagement;
